import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../../components/Button';
import Select from '../../../../components/Select';
import blogStorage from '../../utils/storage';
import { renderMarkdownPreview } from '../../utils/markdown';
import './index.scss';

const ArticleEditor = ({ mode = 'create', articleId = null, onSave = null }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    categoryId: '',
    tags: []
  });
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [isPreview, setIsPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [currentDraftId, setCurrentDraftId] = useState(null); // 当前草稿ID
  
  const autoSaveTimerRef = useRef(null);
  const editorRef = useRef(null);

  // 加载数据
  useEffect(() => {
    loadInitialData();
    
    // 设置自动保存
    const interval = setInterval(autoSave, 30000); // 30秒自动保存
    
    return () => {
      clearInterval(interval);
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // 加载文章数据（编辑模式）
  useEffect(() => {
    if (mode === 'edit' && articleId) {
      loadArticle(articleId);
    }
  }, [mode, articleId]);

  // 更新字数统计
  useEffect(() => {
    setWordCount(blogStorage.countWords(formData.content));
  }, [formData.content]);

  const loadInitialData = () => {
    const categoriesData = blogStorage.getCategories();
    const tagsData = blogStorage.getTags();
    const settings = blogStorage.getSettings();
    
    setCategories(categoriesData);
    setAllTags(tagsData);
    
    // 设置默认分类
    if (categoriesData.length > 0 && !formData.categoryId) {
      const defaultCategoryId = settings?.editorSettings?.defaultCategory || categoriesData[0].id;
      setFormData(prev => ({
        ...prev,
        categoryId: defaultCategoryId
      }));
    }
  };

  const loadArticle = (id) => {
    const articles = blogStorage.getArticles();
    const article = articles.find(art => art.id === id);
    
    if (article) {
      setFormData({
        title: article.title,
        content: article.content,
        categoryId: article.categoryId,
        tags: article.tags || []
      });
    }
  };

  // 处理输入变化
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 触发自动保存延迟
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(autoSave, 5000); // 5秒后自动保存
  };

  // 处理标签输入
  const handleTagInputChange = (e) => {
    setTagInput(e.target.value);
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      
      // 如果是新标签，添加到标签库
      const existingTag = allTags.find(t => t.name === tag);
      if (!existingTag) {
        blogStorage.addTag({ name: tag });
        setAllTags(blogStorage.getTags());
      }
    }
    setTagInput('');
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // 自动保存草稿
  const autoSave = () => {
    if (!formData.title.trim() && !formData.content.trim()) {
      return;
    }

    // 如果是编辑已发布的文章，不进行草稿保存
    if (mode === 'edit' && articleId) {
      return;
    }

    try {
      // 如果有当前草稿ID，更新现有草稿
      if (currentDraftId) {
        blogStorage.updateDraft(currentDraftId, formData);
      } else {
        // 只在创建模式下创建新草稿
        const newDraft = blogStorage.addDraft(formData);
        setCurrentDraftId(newDraft.id);
      }

      setLastSaveTime(new Date());
    } catch (error) {
      console.error('自动保存失败:', error);
    }
  };

  // 手动保存草稿
  const handleSaveDraft = () => {
    if (!formData.title.trim() && !formData.content.trim()) {
      alert('请输入内容后再保存草稿');
      return;
    }

    setIsSaving(true);
    try {
      let draft;
      if (currentDraftId) {
        // 更新现有草稿
        draft = blogStorage.updateDraft(currentDraftId, formData);
        alert('草稿已更新');
      } else {
        // 创建新草稿
        draft = blogStorage.addDraft(formData);
        setCurrentDraftId(draft.id);
        alert('草稿已保存');
      }
      
      if (draft) {
        setLastSaveTime(new Date());
        // 不清空表单，让用户继续编辑
        if (onSave) onSave(draft);
      }
    } catch (error) {
      console.error('保存草稿失败:', error);
      alert('保存失败，请重试');
    } finally {
      setIsSaving(false);
    }
  };

  // 发布文章
  const handlePublish = () => {
    if (!formData.title.trim()) {
      alert('请输入文章标题');
      return;
    }

    if (!formData.content.trim()) {
      alert('请输入文章内容');
      return;
    }

    if (!formData.categoryId) {
      alert('请选择文章分类');
      return;
    }

    setIsSaving(true);
    try {
      if (mode === 'edit' && articleId) {
        // 更新文章
        const updated = blogStorage.updateArticle(articleId, formData);
        if (updated) {
          alert('文章更新成功');
          if (onSave) onSave(updated);
        }
      } else {
        // 新建文章
        const article = blogStorage.addArticle(formData);
        if (article) {
          alert('文章发布成功');
          // 如果是从草稿发布，删除对应草稿
          if (currentDraftId) {
            blogStorage.deleteDraft(currentDraftId);
            setCurrentDraftId(null);
          }
          // 清空表单
          setFormData({
            title: '',
            content: '',
            categoryId: categories[0]?.id || '',
            tags: []
          });
          if (onSave) onSave(article);
          // 跳转到内容列表页面
          navigate('/blog/articles');
        }
      }
    } catch (error) {
      console.error('发布文章失败:', error);
      alert('发布失败，请重试');
    } finally {
      setIsSaving(false);
    }
  };

  // 预览模式
  const togglePreview = () => {
    setIsPreview(!isPreview);
  };

  // 使用新的markdown预览渲染器
  const renderPreview = (content) => {
    return renderMarkdownPreview(content);
  };

  return (
    <div className="article-editor">
      {/* 顶部工具栏 */}
      <div className="editor-toolbar">
        <div className="toolbar-left">
          <div className="editor-stats">
            <span className="word-count">{wordCount} 字</span>
            {lastSaveTime && (
              <span className="last-save">
                上次保存: {lastSaveTime.toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
        
        <div className="toolbar-right">
          <Button 
            className={isPreview ? 'active' : ''}
            onClick={togglePreview}
          >
            {isPreview ? '编辑' : '预览'}
          </Button>
          <Button 
            onClick={handleSaveDraft}
            disabled={isSaving}
          >
            {isSaving ? '保存中...' : '保存草稿'}
          </Button>
          <Button 
            type="primary"
            onClick={handlePublish}
            disabled={isSaving}
          >
            {isSaving ? '发布中...' : mode === 'edit' ? '更新文章' : '发布文章'}
          </Button>
        </div>
      </div>

      <div className="editor-content">
        {!isPreview ? (
          <div className="editor-form">
            {/* 文章信息区块 */}
            <div className="module-section module-info article-info-section">
              <div className="section-header">
                <h4>文章信息</h4>
              </div>
              <div className="section-content">
                {/* 标题输入 */}
                <div className="form-group">
                  <label>文章标题</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="请输入文章标题..."
                    className="title-input"
                  />
                </div>

                {/* 分类和标签 */}
                <div className="form-meta">
                  <div className="form-group">
                    <label>文章分类</label>
                    <Select
                      value={formData.categoryId}
                      onChange={(value) => setFormData(prev => ({ ...prev, categoryId: value }))}
                      options={[
                        { value: '', label: '请选择分类' },
                        ...categories.map(category => ({
                          value: category.id,
                          label: category.parentId ? `└ ${category.name}` : category.name
                        }))
                      ]}
                      placeholder="请选择分类"
                      dropdownPortal={true}
                    />
                  </div>

                  <div className="form-group tags-group">
                    <label>文章标签</label>
                    <div className="tags-input-wrapper">
                      <div className="tags-list">
                        {formData.tags.map(tag => (
                          <span key={tag} className="tag-item">
                            {tag}
                            <button 
                              type="button"
                              onClick={() => removeTag(tag)}
                              className="tag-remove"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                        <input
                          type="text"
                          value={tagInput}
                          onChange={handleTagInputChange}
                          onKeyDown={handleTagInputKeyDown}
                          onBlur={addTag}
                          placeholder="添加标签..."
                          className="tag-input"
                        />
                      </div>
                    </div>
                    {allTags.length > 0 && (
                      <div className="tags-suggestions">
                        <span className="suggestions-label">常用标签：</span>
                        {allTags.slice(0, 8).map(tag => (
                          <button
                            key={tag.id}
                            type="button"
                            onClick={() => {
                              if (!formData.tags.includes(tag.name)) {
                                setFormData(prev => ({
                                  ...prev,
                                  tags: [...prev.tags, tag.name]
                                }));
                              }
                            }}
                            className="tag-suggestion"
                          >
                            {tag.name} ({tag.articleCount})
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* 内容编辑区块 */}
            <div className="module-section module-editor content-editor-section">
              <div className="section-header">
                <h4>文章内容</h4>
                <div className="editor-tips">
                  <span>支持 Markdown 语法</span>
                </div>
              </div>
              <div className="section-content">
                <div className="form-group">
                  <textarea
                    ref={editorRef}
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    placeholder="开始写作吧..."
                    className="content-editor"
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="editor-preview">
            <div className="preview-header">
              <h1 className="preview-title">{formData.title || '未命名文章'}</h1>
              <div className="preview-meta">
                <span className="preview-category">
                  分类: {categories.find(c => c.id === formData.categoryId)?.name || '未分类'}
                </span>
                {formData.tags.length > 0 && (
                  <div className="preview-tags">
                    标签: {formData.tags.map(tag => (
                      <span key={tag} className="preview-tag">{tag}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div 
              className="preview-content"
              dangerouslySetInnerHTML={{ 
                __html: renderPreview(formData.content) || '<p>暂无内容</p>' 
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleEditor;
